import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Calendar, Users, DollarSign, Ticket, Edit, Trash2, Eye, LogOut, BarChart3, Menu } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { getAdminStats, getAdminEvents, deleteEvent, verifyAdmin } from '@/lib/api';
import { formatDate, formatPrice } from '@/lib/utils';
import { toast } from 'sonner';

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }
      try {
        await verifyAdmin();
      } catch (e) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, eventsRes] = await Promise.all([
        getAdminStats(),
        getAdminEvents()
      ]);
      setStats(statsRes.data);
      setEvents(eventsRes.data.events || []);
    } catch (e) {
      console.error('Failed to fetch admin data:', e);
      if (e.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (eventId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This will also remove all attendees for this event.`)) return;
    try {
      await deleteEvent(eventId);
      toast.success('Event deleted successfully');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete event');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <div className="h-8 w-8 rounded-full bg-[color:var(--pm-navy)] flex items-center justify-center">
            <span className="text-xs font-semibold text-[color:var(--pm-surface)] font-heading">PM</span>
          </div>
          <span className="font-heading text-sm font-semibold text-[color:var(--pm-ink)]">Admin Panel</span>
        </div>
        <p className="text-xs text-muted-foreground">{localStorage.getItem('admin_email')}</p>
      </div>
      <Separator />
      <nav className="flex-1 p-3 space-y-1">
        <Link
          to="/admin"
          onClick={() => setSidebarOpen(false)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[color:var(--pm-surface-2)] text-[color:var(--pm-ink)] text-sm font-medium"
        >
          <BarChart3 className="h-4 w-4" />
          Dashboard
        </Link>
        <Link
          to="/admin/analytics"
          onClick={() => setSidebarOpen(false)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:bg-[color:var(--pm-surface-2)] hover:text-[color:var(--pm-ink)] text-sm transition-colors"
        >
          <BarChart3 className="h-4 w-4" />
          Analytics
        </Link>
        <Link
          to="/admin/launch-team"
          onClick={() => setSidebarOpen(false)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:bg-[color:var(--pm-surface-2)] hover:text-[color:var(--pm-ink)] text-sm transition-colors"
        >
          <Users className="h-4 w-4" />
          NYC Launch Team
        </Link>
        <Link
          to="/admin/visit-info"
          onClick={() => setSidebarOpen(false)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:bg-[color:var(--pm-surface-2)] hover:text-[color:var(--pm-ink)] text-sm transition-colors"
        >
          <Users className="h-4 w-4" />
          Visit Info
        </Link>
        <Link
          to="/admin/events/new"
          onClick={() => setSidebarOpen(false)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:bg-[color:var(--pm-surface-2)] hover:text-[color:var(--pm-ink)] text-sm transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create Event
        </Link>
        <Link
          to="/"
          onClick={() => setSidebarOpen(false)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:bg-[color:var(--pm-surface-2)] hover:text-[color:var(--pm-ink)] text-sm transition-colors"
        >
          <Eye className="h-4 w-4" />
          View Site
        </Link>
      </nav>
      <div className="p-3">
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="w-full justify-start text-sm text-muted-foreground hover:text-destructive"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[color:var(--pm-surface)] flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--pm-surface)]">
      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex lg:w-60 lg:flex-col lg:fixed lg:inset-y-0 border-r border-[color:rgba(11,18,32,0.08)] bg-white">
          <SidebarContent />
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:pl-60">
          {/* Top Bar */}
          <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-[color:rgba(11,18,32,0.08)] px-4 sm:px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                  <SheetTrigger asChild>
                    <Button variant="ghost" size="icon" className="lg:hidden" data-testid="admin-sidebar-open-button">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-60 p-0 bg-white">
                    <SidebarContent />
                  </SheetContent>
                </Sheet>
                <h1 className="font-heading text-xl font-semibold text-[color:var(--pm-ink)]">Dashboard</h1>
              </div>
              <Link to="/admin/events/new">
                <Button
                  className="h-9 px-4 rounded-lg bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] text-sm"
                  data-testid="admin-create-event-button"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New Event
                </Button>
              </Link>
            </div>
          </div>

          <div className="p-4 sm:p-6 lg:p-8">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card className="rounded-xl border border-[color:rgba(11,18,32,0.10)] bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Calendar className="h-4 w-4" />
                    <span className="text-xs">Events</span>
                  </div>
                  <p className="font-heading text-2xl font-semibold text-[color:var(--pm-ink)]">
                    {stats?.total_events || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">{stats?.published_events || 0} published</p>
                </CardContent>
              </Card>
              <Card className="rounded-xl border border-[color:rgba(11,18,32,0.10)] bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Ticket className="h-4 w-4" />
                    <span className="text-xs">Tickets Sold</span>
                  </div>
                  <p className="font-heading text-2xl font-semibold text-[color:var(--pm-ink)]">
                    {stats?.tickets_sold || 0}
                  </p>
                </CardContent>
              </Card>
              <Card className="rounded-xl border border-[color:rgba(11,18,32,0.10)] bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <Users className="h-4 w-4" />
                    <span className="text-xs">Attendees</span>
                  </div>
                  <p className="font-heading text-2xl font-semibold text-[color:var(--pm-ink)]">
                    {stats?.total_attendees || 0}
                  </p>
                </CardContent>
              </Card>
              <Card className="rounded-xl border border-[color:rgba(11,18,32,0.10)] bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground mb-1">
                    <DollarSign className="h-4 w-4" />
                    <span className="text-xs">Revenue</span>
                  </div>
                  <p className="font-heading text-2xl font-semibold text-[color:var(--pm-ink)]">
                    {formatPrice(stats?.total_revenue || 0)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Events Table */}
            <Card className="rounded-xl border border-[color:rgba(11,18,32,0.10)] bg-white">
              <CardHeader className="px-4 sm:px-6 py-4">
                <CardTitle className="font-heading text-lg font-semibold text-[color:var(--pm-ink)]">All Events</CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-4">
                {events.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table data-testid="admin-events-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="pl-4 sm:pl-6">Event</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Tickets</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right pr-4 sm:pr-6">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {events.map(event => (
                          <TableRow key={event.id}>
                            <TableCell className="pl-4 sm:pl-6 font-medium text-[color:var(--pm-ink)] max-w-[200px] truncate">
                              {event.title}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="rounded-full text-xs">
                                {event.category}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(event.start_at)}
                            </TableCell>
                            <TableCell className="font-heading font-medium">
                              {formatPrice(event.price, event.currency)}
                            </TableCell>
                            <TableCell className="text-sm">
                              {event.tickets_sold || 0} / {event.capacity}
                            </TableCell>
                            <TableCell>
                              <Badge
                                className={`rounded-full text-xs ${
                                  event.status === 'published'
                                    ? 'bg-[color:var(--pm-success)]/10 text-[color:var(--pm-success)]'
                                    : 'bg-muted text-muted-foreground'
                                }`}
                              >
                                {event.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right pr-4 sm:pr-6">
                              <div className="flex items-center justify-end gap-1">
                                <Link to={`/admin/events/${event.id}/attendees`}>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" title="View Attendees">
                                    <Users className="h-4 w-4" />
                                  </Button>
                                </Link>
                                <Link to={`/admin/events/${event.id}/edit`}>
                                  <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit">
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                </Link>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive hover:text-destructive"
                                  onClick={() => handleDelete(event.id, event.title)}
                                  title="Delete"
                                  data-testid="admin-event-delete-button"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="h-10 w-10 mx-auto text-muted-foreground" />
                    <p className="font-heading text-lg text-[color:var(--pm-ink)] mt-3">No events yet</p>
                    <p className="text-sm text-muted-foreground">Create your first event to get started.</p>
                    <Link to="/admin/events/new">
                      <Button className="mt-4 rounded-lg">
                        <Plus className="h-4 w-4 mr-1" />
                        Create Event
                      </Button>
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
}
